<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import * as Sentry from '@sentry/vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { ALERT_TONES, ALERT_TONE_GROUPS, alertToneUrl } from './constants';

const props = defineProps({
  value: {
    type: String,
    required: true,
    validator: value => ALERT_TONES.includes(value),
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);
const { t } = useI18n();

const I18N_KEY = 'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION';
const GROUP_ICONS = {
  MESSAGES: 'i-hugeicons-message-notification-01',
  CONFIRMATION: 'i-hugeicons-checkmark-circle-02',
  MELODIC: 'i-hugeicons-music-note-01',
  CLASSIC: 'i-hugeicons-notification-01',
  MODERN: 'i-hugeicons-droplet',
  RETRO: 'i-hugeicons-game-controller-03',
  RPG: 'i-hugeicons-magic-wand-01',
};

const dialogRef = ref(null);
// Tone under audition in the modal: only persisted when the person clicks Save.
const draftTone = ref(props.value);
const search = ref('');
const activeGroup = ref('ALL');
const playingTone = ref('');

const normalize = text =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const groups = computed(() =>
  ALERT_TONE_GROUPS.map(group => ({
    key: group.key,
    icon: GROUP_ICONS[group.key] || 'i-hugeicons-notification-01',
    label: t(`${I18N_KEY}.TONE_GROUPS.${group.key}`),
    tones: group.tones.map(tone => ({
      value: tone,
      label: t(`${I18N_KEY}.TONES.${tone.toUpperCase()}`),
    })),
  }))
);

const chips = computed(() => [
  {
    key: 'ALL',
    label: t(`${I18N_KEY}.TONE_PICKER.ALL`),
    icon: 'i-hugeicons-grid-view',
  },
  ...groups.value,
]);

const describeTone = value => {
  const group = groups.value.find(item =>
    item.tones.some(tone => tone.value === value)
  );
  return {
    label: group?.tones.find(tone => tone.value === value)?.label,
    group,
  };
};

const selectedTone = computed(() => describeTone(props.value));
const draftDescription = computed(() => describeTone(draftTone.value));

const visibleGroups = computed(() => {
  const term = normalize(search.value.trim());
  return groups.value
    .filter(
      group => activeGroup.value === 'ALL' || group.key === activeGroup.value
    )
    .map(group => ({
      ...group,
      tones: group.tones.filter(
        tone =>
          !term ||
          normalize(tone.label).includes(term) ||
          normalize(group.label).includes(term)
      ),
    }))
    .filter(group => group.tones.length);
});

const audio = new Audio();
audio.addEventListener?.('ended', () => {
  playingTone.value = '';
});

// Preview only runs on an explicit click, respecting autoplay restrictions.
const play = async tone => {
  try {
    audio.pause();
    audio.src = alertToneUrl(tone);
    audio.currentTime = 0;
    playingTone.value = tone;
    await audio.play();
  } catch (error) {
    playingTone.value = '';
    Sentry.captureException(error);
  }
};

// Choosing a tone in the modal only changes the draft and plays it, so the
// person can compare tones; nothing is saved until Save is clicked.
const choose = tone => {
  draftTone.value = tone;
  play(tone);
};

const openPicker = () => {
  draftTone.value = props.value;
  search.value = '';
  activeGroup.value = 'ALL';
  dialogRef.value?.open();
};

// Cancel, Escape and clicking outside keep the saved tone.
const handleClose = () => {
  audio.pause();
  playingTone.value = '';
  draftTone.value = props.value;
};

const save = () => {
  const tone = draftTone.value;
  if (tone !== props.value) emit('change', tone);
  dialogRef.value?.close();
};

onBeforeUnmount(() => audio.pause());
</script>

<template>
  <section class="grid gap-3">
    <span v-if="label" class="text-sm font-medium text-n-slate-12">
      {{ label }}
    </span>
    <div
      class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
    >
      <span
        class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-brand/10 text-n-brand"
        aria-hidden="true"
      >
        <Icon
          :icon="selectedTone.group?.icon || 'i-hugeicons-notification-01'"
          class="size-5"
        />
      </span>
      <div class="min-w-0 flex-1">
        <span class="block text-xs text-n-slate-11">
          {{ $t(`${I18N_KEY}.TONE_PICKER.CURRENT`) }}
        </span>
        <strong class="block truncate text-sm text-n-slate-12">
          {{ selectedTone.label }}
        </strong>
        <span v-if="selectedTone.group" class="block text-xs text-n-slate-11">
          {{ selectedTone.group.label }}
        </span>
      </div>
      <div class="flex shrink-0 gap-2">
        <Button
          v-tooltip.top="$t(`${I18N_KEY}.PLAY`)"
          type="button"
          icon="i-hugeicons-volume-high"
          color="slate"
          variant="outline"
          size="sm"
          :aria-label="$t(`${I18N_KEY}.PLAY`)"
          data-test-id="alert-tone-preview"
          @click="play(value)"
        />
        <Button
          type="button"
          :label="$t(`${I18N_KEY}.TONE_PICKER.CHANGE`)"
          icon="i-hugeicons-music-note-01"
          size="sm"
          aria-haspopup="dialog"
          data-test-id="alert-tone-toggle"
          @click="openPicker"
        />
      </div>
    </div>

    <Dialog
      ref="dialogRef"
      solid
      width="3xl"
      overflow-y-auto
      sticky-header-footer
      :title="$t(`${I18N_KEY}.TONE_PICKER.TITLE`)"
      :description="$t(`${I18N_KEY}.TONE_PICKER.DESCRIPTION`)"
      :confirm-button-label="$t(`${I18N_KEY}.TONE_PICKER.SAVE`)"
      @confirm="save"
      @close="handleClose"
    >
      <div id="alert-tone-picker" class="grid gap-4">
        <div
          class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
          aria-live="polite"
          data-test-id="alert-tone-draft"
        >
          <Icon
            :icon="
              draftDescription.group?.icon || 'i-hugeicons-notification-01'
            "
            class="size-4 shrink-0 text-n-brand"
          />
          <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
            {{ $t(`${I18N_KEY}.TONE_PICKER.DRAFT`) }}
            <strong>{{ draftDescription.label }}</strong>
          </span>
          <span
            v-if="draftTone !== value"
            class="shrink-0 rounded-full bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11"
          >
            {{ $t(`${I18N_KEY}.TONE_PICKER.UNSAVED`) }}
          </span>
        </div>
        <Input
          v-model="search"
          type="search"
          :placeholder="$t(`${I18N_KEY}.TONE_PICKER.SEARCH`)"
          :aria-label="$t(`${I18N_KEY}.TONE_PICKER.SEARCH`)"
        />
        <div class="flex gap-2 overflow-x-auto pb-1" :aria-label="label">
          <button
            v-for="chip in chips"
            :key="chip.key"
            type="button"
            :aria-pressed="activeGroup === chip.key"
            class="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :class="
              activeGroup === chip.key
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            :data-test-id="`alert-tone-group-${chip.key}`"
            @click="activeGroup = chip.key"
          >
            <Icon :icon="chip.icon" class="size-3.5" />
            {{ chip.label }}
          </button>
        </div>

        <p v-if="!visibleGroups.length" class="mb-0 text-sm text-n-slate-11">
          {{ $t(`${I18N_KEY}.TONE_PICKER.EMPTY`) }}
        </p>
        <section
          v-for="group in visibleGroups"
          :key="group.key"
          class="grid gap-2"
          :aria-label="group.label"
        >
          <h4
            class="mb-0 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ group.label }}
            <span class="font-normal normal-case tracking-normal">
              ·
              {{
                $t(`${I18N_KEY}.TONE_PICKER.COUNT`, {
                  count: group.tones.length,
                })
              }}
            </span>
          </h4>
          <div
            class="grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
            role="radiogroup"
            :aria-label="group.label"
          >
            <div
              v-for="tone in group.tones"
              :key="tone.value"
              class="flex min-w-0 items-center gap-2 rounded-lg border p-1.5 transition-colors"
              :class="
                tone.value === draftTone
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak hover:bg-n-alpha-2'
              "
            >
              <button
                type="button"
                class="grid size-8 shrink-0 place-content-center rounded-full bg-n-alpha-2 text-n-slate-12 hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :aria-label="
                  $t(`${I18N_KEY}.TONE_PICKER.PLAY_TONE`, { name: tone.label })
                "
                :data-test-id="`alert-tone-play-${tone.value}`"
                @click="play(tone.value)"
              >
                <Icon
                  :icon="
                    playingTone === tone.value
                      ? 'i-hugeicons-volume-high'
                      : 'i-hugeicons-play'
                  "
                  class="size-4"
                  :class="{
                    'motion-safe:animate-pulse': playingTone === tone.value,
                  }"
                />
              </button>
              <button
                type="button"
                role="radio"
                :aria-checked="tone.value === draftTone"
                :aria-label="
                  $t(`${I18N_KEY}.TONE_PICKER.SELECT_TONE`, {
                    name: tone.label,
                  })
                "
                class="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-1 py-1 text-left text-sm text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                :data-test-id="`alert-tone-option-${tone.value}`"
                @click="choose(tone.value)"
              >
                <span class="truncate">{{ tone.label }}</span>
                <Icon
                  v-if="tone.value === draftTone"
                  icon="i-hugeicons-checkmark-circle-02"
                  class="size-4 shrink-0 text-n-brand"
                />
              </button>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  </section>
</template>
